import { apiConfig } from '../services/api/apiConfig';

const saveBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Download a contract file.
 *
 * A plain link to the API can't carry the login token, so ask the API for a
 * short-lived link first (with the token) and let the browser open that.
 * Files still on the server's disk from before the Spaces migration have no
 * such link, so they're fetched through the API instead.
 */
export const downloadContractFile = async (token: string, fileId: string, filename: string): Promise<void> => {
  const headers = { Authorization: `Bearer ${token}` };
  const response = await fetch(`${apiConfig.baseURL}/api/contracts/files/${fileId}/download-url`, { headers });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body.success === false) {
    throw new Error(body.message || `Download failed (${response.status})`);
  }

  const url: string | null = body.data?.url ?? null;
  if (url) {
    // The link already tells the browser to save it under the original name.
    const link = document.createElement('a');
    link.href = url;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
    return;
  }

  const fileResponse = await fetch(
    `${apiConfig.baseURL}${apiConfig.endpoints.contracts.downloadFile(fileId)}`,
    { headers }
  );
  if (fileResponse.status === 404) {
    // The record exists but the bytes are on another machine's disk: typically
    // a local app pointed at the live database before `npm run migrate:uploads`
    // has moved the old uploads into cloud storage.
    throw new Error(
      `${filename} is stored on the server's disk from before the move to cloud storage, so it can't be downloaded from here. It downloads from the live app, and from anywhere once the uploads are migrated.`
    );
  }
  if (!fileResponse.ok) throw new Error(`Download failed (${fileResponse.status})`);
  saveBlob(await fileResponse.blob(), body.data?.filename || filename);
};
